<template>
    <div class="trips-search min-vh-100">
        <div class="nav-container top-container">
            <div class="container-fluid">
                <div class="d-flex align-items-center mb-3 mt-n3">
                    <a href="/" class="btn btn-link text-dark btn-sm">
                        <i class="fa fa-chevron-left mr-1"></i>
                        {{ 'search.back' | trans }}
                    </a>

                    <div class="btn-group ml-auto" role="group" aria-label="Button group with nested dropdown">
                        <!-- Toggle template -->
                        <button type="button" class="btn btn-light btn-default" :class="{ 'text-primary': currentSearch.is_template }" @click="toggleTemplate" v-if="currentSearch && additionalFilters.indexOf('create_templates') !== -1">
                            <span v-if="! currentSearch.is_template">
                                <i class="fad fa-clone mr-1"></i>
                                {{ 'search.useAsTemplate' | trans }}
                            </span>

                            <span v-if="currentSearch.is_template">
                                <i class="fad fa-check-double mr-1"></i>
                                {{ 'search.template' | trans }}
                            </span>
                        </button>

                        <!-- History -->
                        <button type="button" class="btn btn-light btn-default" @click="isShowHistory = ! isShowHistory" v-if="currentSearch && currentSearch.regions && currentSearch.regions.length > 0 && ! isCopied">
                            <i class="fad fa-history mr-1"></i>
                            <!-- Verlauf -->
                        </button>

                        <!-- Copy link -->
                        <button type="button" class="btn btn-light btn-default" @click="copyLink">
                            <span v-show="! isCopied">
                                <i class="fad fa-copy mr-1"></i>
                                <!-- Link kopieren -->
                            </span>
                            <span v-show="isCopied">
                                <span class="emoji mr-1">🎉</span>
                                {{ 'search.copied' | trans }}
                            </span>
                        </button>
                    </div>
                </div>

                <!-- Search bar -->
                <nav class="search-navbar" v-show="currentSearch">
                    <!-- Query -->
                    <LocationInput
                    v-model="query"
                    :location="location"
                    @changeLocation="handleChangeLocation"
                    ref="locationInput" />

                    <!-- Travel period -->
                    <TravelPeriodPicker
                    :origin-period="travelPeriod"
                    @changeTravelPeriod="handleChangeTravelPeriod"
                    ref="travelPeriodPicker" />

                    <!-- Persons picker -->
                    <PersonsPicker
                    :origin-persons="persons"
                    @changePersons="handleChangePersons"
                    ref="personsPicker" />

                    <!-- Search button -->
                    <button type="button" class="btn btn-primary btn-lg search-button" @click="() => search()">
                        <span v-if="! isLoading">
                            <i class="wa-icon-search"></i>
                        </span>
                        <span v-if="! isLoading" class="d-md-none d-lg-inline-block ml-1">
                            {{ 'search.search' | trans }}
                        </span>

                        <svg class="white-loader" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" v-if="isLoading">
                            <circle class="internal-circle" cx="60" cy="60" r="30"></circle>
                            <circle class="external-circle" cx="60" cy="60" r="50"></circle>
                        </svg>
                    </button>
                </nav>
            </div>
        </div>

        <div class="container-fluid">
            <div class="row">
                <div class="col-xl-3 col-lg-5 col-md-4 d-none d-md-inline sidebar-filters">
                    <Filters
                    :is-sidebar="true"
                    :is-loading="isLoading"
                    :is-visible="true"
                    :origin-filters="filters"
                    :property-types="propertyTypes"
                    :amenities="amenities"
                    :additional-filters="additionalFilters"
                    :total="meta && meta.total ? meta.total : 0"
                    :nights="1"
                    @close="handleCloseFilters"
                    @change="handleChangeFilters" />
                </div>

                <!-- Toggle filters view -->
                <div class="mobile-filter-menu d-md-none">
                    <span class="sorting-label">
                        {{ 'search.orderLabel' | trans }}:
                    </span>
                    
                    <select v-model="order">
                        <!-- Recommended -->
                        <option value="recommended">
                            {{ 'search.order.recommended' | trans }}
                        </option>

                        <!-- Ratings -->
                        <option value="ratings">
                            {{ 'search.order.ratings' | trans }}
                        </option>

                        <!-- Price (asc) -->
                        <option value="price_asc" v-if="travelPeriod">
                            {{ 'search.order.priceAsc' | trans }}
                        </option>

                        <!-- Price (desc) -->
                        <option value="price_desc" v-if="travelPeriod">
                            {{ 'search.order.priceDesc' | trans }}
                        </option>

                        <!-- Stars -->
                        <option value="stars">
                            {{ 'search.order.stars' | trans }}
                        </option>

                        <!-- Center distance -->
                        <option value="center_distance" v-if="location">
                            {{ 'search.order.centerDistance' | trans }}
                        </option>
                        
                        <!-- Distance -->
                        <option value="distance" v-if="location && ! location.has_polygon">
                            {{ 'search.order.distance' | trans }}
                        </option>
                    </select>

                    <button type="button" class="btn " @click="() => isShowFilters = ! isShowFilters" :class="{ 'bg-dark text-white border-dark': isShowFilters }">
                        <i class="far fa-filter mr-1" v-if="! isShowFilters"></i>
                        <i class="far fa-times mr-1" v-if="isShowFilters"></i>
                        <span class="count-badge" v-if="! isShowFilters && filtersCount > 0">
                            {{ filtersCount }}
                        </span>

                        {{ 'search.filters.label' | trans }}
                    </button>
                </div>

                <div class="results-list position-relative col-xl-9 col-lg-7 col-md-8">
                    <div class="nav-container pt-4 pb-0">
                        <div class="toolbar fadeIn animated" v-if="currentSearch">
                            <div class="d-flex flex-column" v-if="! isShowFilters">
                                <!-- Property scope -->
                                <!-- <span class="text-muted mb-n1" v-if="filters.property_scope">
                                    <span v-if="filters.property_scope === 'hotels'">
                                        Hotels in
                                    </span>
                                    <span v-if="filters.property_scope === 'homes'">
                                        Ferienwohnungen in
                                    </span>
                                </span> -->

                                <h1 class="mb-0">
                                    {{ location && location.name ? location.name : currentSearch.name }}
                                </h1>

                                <!-- Total -->
                                <span class="total" v-if="meta && meta.total">
                                    <span v-if="meta.total > 300">{{ 'search.metaContainer.over300' | trans }}&nbsp;</span>
                                    <span v-if="meta.total < 300">{{ meta.total }}&nbsp;</span>
                                    {{ 'search.metaContainer.hotelsFound' | trans }}
                                </span>
                            </div>

                            <!-- Filters -->
                            <h5 class="mt-2" v-show="isShowFilters">
                                {{ 'search.filters.moreLabel' | trans }}
                            </h5>
                        </div>

                        <!-- Search regions -->
                        <button type="button" class="btn btn-sm mb-2 text-dark text-left" @click="isShowHistory = ! isShowHistory" v-if="isShowHistory && currentSearch && currentSearch.regions && currentSearch.regions.length > 0">
                            {{ 'search.history' | trans }}
                            <i class="fa fa-chevron-down ml-1 text-muted" v-show="! isShowHistory"></i>
                            <i class="fa fa-chevron-up ml-1 text-muted" v-show="isShowHistory"></i>
                        </button>
                        <div class="search-regions" v-if="currentSearch && currentSearch.regions && currentSearch.regions.length > 0 && isShowHistory">
                            <!-- Region card -->
                            <div class="search-region-card" :class="{ active: activeSearchRegion(region) }" v-for="(region, regionIndex) in currentSearch.regions" @click.prevent="selectSearchRegion(region)">
                                <!-- Close -->
                                <button type="button" class="btn btn-close" @click.prevent.stop="removeSearchRegion(region, regionIndex)">
                                    <span>
                                        <i class="far fa-times"></i>
                                    </span>
                                </button>

                                <!-- Name -->
                                <span class="name">
                                    {{ region.name }}
                                </span>

                                <!-- Travel period -->
                                <span class="travel-period" v-if="region.travel_period">
                                    {{ $moment(region.travel_period.date_from).format('DD.MM') }}
                                    -
                                    {{ $moment(region.travel_period.date_to).format('DD.MM.') }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Sorting -->
                    <div class="btn-group mt-3 d-none d-md-flex sorting-menu" v-if="meta && meta.total > 0">
                        <!-- Recommended -->
                        <button type="button" class="btn btn-default" :class="{ 'active': order === 'recommended' }" @click="() => order = 'recommended'">
                            {{ 'search.order.recommended' | trans }}
                        </button>

                        <!-- Ratings -->
                        <button type="button" class="btn btn-default" :class="{ 'active': order === 'ratings' }" @click="() => order = 'ratings'">
                            {{ 'search.order.ratings' | trans }}
                        </button>

                        <!-- Price (asc) -->
                        <button type="button" class="btn btn-default" :class="{ 'active': order === 'price_asc' }" @click="() => order = 'price_asc'" v-if="travelPeriod">
                            {{ 'search.order.priceAsc' | trans }}
                        </button>

                        <!-- Price (desc) -->
                        <button type="button" class="btn btn-default" :class="{ 'active': order === 'price_desc' }" @click="() => order = 'price_desc'" v-if="travelPeriod && screenWidth > 992">
                            {{ 'search.order.priceDesc' | trans }}
                        </button>

                        <!-- Stars -->
                        <button type="button" class="btn btn-default" :class="{ 'active': order === 'stars' }" @click="() => order = 'stars'" v-if="screenWidth > 1200">
                            {{ 'search.order.stars' | trans }}
                        </button>

                        <div class="btn-group" role="group" v-if="location || screenWidth <= 1200">
                            <button id="btnGroupDrop1" type="button" class="btn btn-default" data-toggle="dropdown" aria-expanded="false">
                                <i class="fa fa-caret-down"></i>
                            </button>
                            
                            <div class="dropdown-menu dropdown-menu-right" aria-labelledby="btnGroupDrop1">
                                <!-- Price (desc) -->
                                <a href="#" class="dropdown-item font-weight-500 pt-2" :class="{ 'active': order === 'price_desc' }" @click="() => order = 'price_desc'" v-if="screenWidth <= 992">
                                    {{ 'search.order.priceDesc' | trans }}
                                </a>

                                <!-- Stars -->
                                <a href="#" class="dropdown-item font-weight-500 pt-2" :class="{ 'active': order === 'stars' }" @click="() => order = 'stars'" v-if="screenWidth <= 1200">
                                    {{ 'search.order.stars' | trans }}
                                </a>

                                <!-- Center distance -->
                                <a href="#" class="dropdown-item font-weight-500 pt-2" :class="{ 'active': order === 'center_distance' }" @click="() => order = 'center_distance'" v-if="location">
                                    {{ 'search.order.centerDistance' | trans }}
                                </a>

                                <!-- Distance -->
                                <a href="#" class="dropdown-item font-weight-500 pt-2" :class="{ 'active': order === 'distance' }" @click="() => order = 'distance'" v-if="location && ! location.has_polygon">
                                    {{ 'search.order.distance' | trans }}
                                </a>
                            </div>
                        </div>
                    </div>

                    <!-- Search loading placeholder -->
                    <LoadingPlaceholder class="mt-3" v-show="isLoading && ! isShowFilters && currentView === 'search' && currentSearch" />

                    <!-- Search result -->
                    <div class="search-result mt-3" v-if="currentSearch && currentView === 'search' && ! isShowFilters" v-show="! isLoading">
                        <!-- Price info -->
                        <span class="price-info" v-if="travelPeriod !== null && ! isHidePriceInfo" @click="isHidePriceInfo = true">
                            <i class="wa-icon-close"></i>
                            <i class="wa-icon-euro-circle"></i>
                            {{ 'search.priceInfo.firstLine' | trans }}<br />
                            {{ 'search.priceInfo.secondLine' | trans }}
                        </span>

                        <!-- Select travel period -->
                        <div v-show="travelPeriod === null">
                            <EmptyMessage
                            image="https://d36xws8ymuqmas.cloudfront.net/illustrations/search.svg?raw=true"
                            :title="'search.emptyMessages.pickTravelPeriodTitle' | trans"
                            :subtitle="'search.emptyMessages.pickTravelPeriodSubtitle' | trans" />

                            <div class="divider"></div>
                        </div>

                        <!-- Warning for template usage -->
                        <div class="alert alert-warning" v-if="currentSearch && currentSearch.is_template">
                            <strong class="d-block mb-3">
                                {{ 'search.templateUsageWarning.title' | trans }}
                            </strong>
                            
                            {{ 'search.templateUsageWarning.info' | trans({ name: currentSearch.name }) }}
                        </div>

                        <!-- Highlighted property -->
                        <div v-if="highlightedProperties && highlightedProperties.length > 0">
                            <PropertyList
                            ref="highlightedPropertyList"
                            :highlighted="true"
                            :search-token="token"
                            :items="highlightedProperties"
                            :filters="filters"
                            :travelPeriod="travelPeriod"
                            @centerProperty="handleCenterProperty"
                            @toggleBookmarkProperty="handleToggleBookmarkProperty"
                            :isTravelPeriod="travelPeriod !== null" />

                            <div v-if="data && data.length > 0">
                                <span class="section-title mt-3">
                                    {{ 'search.propertyList.otherHotels' | trans }}
                                </span>
                                <div class="divider mt-2 mb-3"></div>
                            </div>
                        </div>

                        <!-- Properties list -->
                        <PropertyList
                        ref="propertyList"
                        :search-token="token"
                        :items="data"
                        :filters="filters"
                        :travelPeriod="travelPeriod"
                        @centerProperty="handleCenterProperty"
                        @toggleBookmarkProperty="handleToggleBookmarkProperty"
                        :isTravelPeriod="travelPeriod !== null" />

                        <!-- Empty view -->
                        <EmptyMessage
                        v-if="meta && meta.total === 0 && highlightedProperties.length === 0"
                        class="mt-4"
                        image="https://d36xws8ymuqmas.cloudfront.net/illustrations/search.svg?raw=true"
                        :title="'search.emptyMessages.noAvailableHotelsTitle' | trans"
                        :subtitle="'search.emptyMessages.noAvailableHotelsSubtitle' | trans" />

                        <!-- Pagination -->
                        <nav class="pagination-control" aria-label="Pagination control" v-if="currentPage && meta && meta.total > 0">
                            <button type="button" class="btn btn-chevron" :disabled="currentPage === 1" @click.prevent="prevPage">
                                <i class="fa fa-chevron-left"></i>
                            </button>

                            <button type="button" class="btn btn-page" :class="{ 'current-page': currentPage === page }" v-for="page in pages" @click.prevent="setPage(page)">
                                {{ page }}
                            </button>

                            <button type="button" class="btn btn-chevron" :disabled="currentPage === meta.last_page" @click.prevent="nextPage">
                                <i class="fa fa-chevron-right"></i>
                            </button>
                        </nav>
                    </div>

                    <Filters
                    :is-loading="isLoading"
                    :is-visible="isShowFilters"
                    :origin-filters="filters"
                    :property-types="propertyTypes"
                    :amenities="amenities"
                    :additional-filters="additionalFilters"
                    :total="meta && meta.total ? meta.total : 0"
                    :nights="1"
                    @close="handleCloseFilters"
                    @change="handleChangeFilters" />

                    <!-- Search bookmarks -->
                    <div class="search-bookmarks" v-if="currentView === 'bookmarks' && currentSearch">
                        <!-- Empty view -->
                        <EmptyMessage v-if="currentSearch.bookmarks.length === 0"
                        image="https://d36xws8ymuqmas.cloudfront.net/illustrations/easy.svg?raw=true"
                        :title="'search.emptyMessages.emptyBookmarksTitle' | trans"
                        :subtitle="'search.emptyMessages.emptyBookmarksSubtitle' | trans"
                        :button-text="'search.emptyMessages.emptyBookmarksButton' | trans"
                        :buttonAction="currentView = 'search'" />

                        <div class="meta-container mt-3" v-if="currentSearch.bookmarks.length > 0">
                            <!-- Total -->
                            <span class="total">
                                {{ 'search.metaContainer.bookmarksCount' | choice(currentSearch.bookmarks.length, { count: currentSearch.bookmarks.length }) }}
                            </span>

                            <!-- Info -->
                            <span class="price-info">
                                <i class="wa-icon-favorites-heart"></i>
                                {{ 'search.bookmarksInfo' | trans }}
                            </span>
                        </div>

                        <PropertyList
                        :search-token="token"
                        v-if="currentSearch.bookmarks.length > 0"
                        :items="bookmarkedHotels"
                        @toggleBookmarkProperty="handleToggleBookmarkProperty" />
                    </div>

                    <!-- Activities result -->
                    <div class="activities-result" v-if="currentView === 'activities' && currentSearch">
                        <div class="meta-container" v-if="meta && meta.total">
                            <!-- Total -->
                            <span class="total">
                                {{ activities.length }}&nbsp;
                                Aktivitäten gefunden.
                            </span>

                            <!-- Price info -->
                            <span class="price-info" v-if="travelPeriod !== null">
                                <i class="wa-icon-euro-circle"></i>
                                Alle Preise enthalten bereits Steuern und Gebühren.<br />
                                Die Touristensteuer ist vor Ort zu zahlen. Wir zeigen sie dir vorher an.
                            </span>
                        </div>

                        <h5 class="pb-3 border-bottom mb-3">
                            Geführte Touren
                        </h5>
                        
                        <div class="activity-items-container">
                            <ActivityCard
                            v-for="activity in activities"
                            :item="activity"
                            :key="'activity_' + activity.id" />
                        </div>

                        <h5 class="pb-3 border-bottom mt-5 mb-3">
                            Stadtführungen
                        </h5>
                        
                        <div class="activity-items-container">
                            <ActivityCard
                            v-for="activity in activities"
                            :item="activity"
                            :key="'activity_' + activity.id" />
                        </div>
                    </div>

                    <!-- Toggle view -->
                    <div class="toggle-view btn-group btn-group-toggle tada animated" data-toggle="buttons" v-show="currentSearch && (views.length > 1 || currentView === 'bookmarks') && ! isShowFilters">
                        <label class="btn btn-link" :for="'view_' + view" :class="{ active: view === currentView }" v-for="view in views" @click="currentView = view">
                            <input type="radio" name="view" :id="'view_' + view" v-model="currentView" :value="view">
                            {{ 'search.views.' + view | trans }}
                        </label>
                    </div>
                </div>

                <!-- Region ID -->
                <small class="text-muted d-block mt-2 text-right w-100 mr-3" v-if="location && location.id" @click.prevent="copy('https://trips.urlaubsguru.de/suchen?region_id=' + location.id)" style="cursor: pointer;">
                    <b>Region</b>
                    {{ location.id }}
                </small>

                <!-- Map -->
                <!-- <div class="col-xl-6 col-lg-5 col-md-12 pl-0" v-if="currentSearch" v-show="isMapVisible">
                    <div style="height: 100%;">
                        <Map
                        :center-property="centerProperty"
                        :places="data"
                        @loadMapProperties="handleLoadMapProperties" />
                    </div>
                </div> -->
            </div>
        </div>

        <!-- Loader -->
        <div class="container py-5 text-center" v-if="! currentSearch">
            <svg class="loader" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
                <circle class="internal-circle" cx="60" cy="60" r="30"></circle>
                <circle class="external-circle" cx="60" cy="60" r="50"></circle>
            </svg>
        </div>
    </div>
</template>

<script>
    import LocationInput from './search/LocationInput.vue'
    import TravelPeriodPicker from './search/TravelPeriodPicker.vue'
    import PersonsPicker from './search/PersonsPicker.vue'
    import PropertyList from './search/PropertyList.vue'
    import Map from './search/Map.vue'
    import Filters from './search/Filters.vue'
    import LoadingPlaceholder from './search/LoadingPlaceholder.vue'
    import EmptyMessage from './search/EmptyMessage.vue'

    // Item cards
    import SearchCard from './search/itemCards/Search.vue'
    import RegionCard from './search/itemCards/Region.vue'
    import TransferTypeCard from './search/itemCards/TransferType.vue'
    import PropertyTypeCard from './search/itemCards/PropertyType.vue'
    import PropertyThemeCard from './search/itemCards/PropertyTheme.vue'
    import DisplayPromoCard from './search/itemCards/DisplayPromo.vue'
    import ActivityCard from './search/itemCards/ActivityCard.vue'

    export default {
        components: {
            LocationInput,
            TravelPeriodPicker,
            PersonsPicker,
            PropertyList,
            Map,
            Filters,
            LoadingPlaceholder,
            EmptyMessage,

            // Item cards
            SearchCard,
            RegionCard,
            TransferTypeCard,
            PropertyTypeCard,
            PropertyThemeCard,
            DisplayPromoCard,
            ActivityCard,
        },
        props: ['token'],
        data() {
            return {
                tabs: ['create_search', 'saved_searches', 'search_code'],
                currentTab: 'create_search',
                currentSearch: null,
                isShowHistory: false,
                isShowFilters: false,
                isSkipFirstParamsRefresh: true,
                data: [],
                meta: {},
                highlightedProperties: [],
                currentPage: null,
                centerProperty: null,
                cancelSource: null,
                isLoading: false,
                isCopied: false,
                isMounted: false,
                lastSearch: null,
                screenWidth: null,
                isHidePriceInfo: false,

                // Views
                currentView: 'search',

                // Filters data
                propertyTypes: [],
                amenities: [],
                additionalFilters: [],

                // Filters
                query: '',
                location: null,
                travelPeriod: null,
                persons: [],
                filters: {
                    is_refundable: true,
                    is_package: false,
                    is_member: false,
                    property_types: [],
                    amenities: [],
                    property_scope: null,
                    property_stars: [],
                    ratings: null,
                    price_range: [],
                    distance_to_center: [],
                },
                order: 'recommended',

                // Activities
                activities: [{
                    id: 998,
                    name: '90 Minuten Audio-Guide Tour',
                    image: 'https://images.unsplash.com/photo-1522010675502-c7b3888985f6?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=387&q=80',
                    price: 14.50,
                    duration: 90,
                }, {
                    id: 999,
                    name: '90 Minuten Audio-Guide Tour',
                    image: 'https://images.unsplash.com/photo-1522010675502-c7b3888985f6?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=387&q=80',
                    price: 14.50,
                    duration: 90,
                }, {
                    id: 9999,
                    name: '90 Minuten Audio-Guide Tour',
                    image: 'https://images.unsplash.com/photo-1522010675502-c7b3888985f6?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=387&q=80',
                    price: 14.50,
                    duration: 90,
                }],
            }
        },
        created() {
            window.addEventListener("resize", this.updateSize);
        },
        destroyed() {
            window.removeEventListener("resize", this.updateSize);
        },
        mounted() {
            // Init layout params
            this.screenWidth = $(window).width()

            // Handle initial load from params or use search token
            this.isSkipFirstParamsRefresh = ! this.modificationParamsAvailable()
            if (this.paramsAvailable() || this.modificationParamsAvailable()) {
                this.isLoading = true;
                this.mountParams()

                this.isMounted = true
            } else {
                this.loadSearch()
            }
        },
        computed: {
            pages() {
                if (this.meta && this.meta.from && this.currentPage) {
                    let pages = []
                    for (var i = this.currentPage; i <= this.meta.last_page; i++) {
                        pages.push(i)
                    }

                    if (this.currentPage === 2) {
                        pages = [1, ...pages]
                    }
                    if (this.currentPage > 2) {
                        pages = [pages[0] - 2, pages[0] - 1, ...pages]
                    }

                    // Slice to max five
                    pages = pages.slice(0, 5)

                    if (pages.length < 5) {
                        for (var i = 0; i <= 5 - pages.length; i++) {
                            pages = [pages[0] - i, ...pages]
                        }
                        pages = pages.sort()
                    }

                    // Filter negative values
                    return [...new Set(pages)].filter((page) => page > 0).sort().slice(0, 5).sort()
                }

                return []
            },

            bookmarkedHotels() {
                if (! this.currentSearch || (this.currentSearch && ! this.currentSearch.bookmarks)) {
                    return []
                }

                return this.currentSearch.bookmarks.map((bookmark) => {
                    bookmark.hotel.is_bookmarked = true

                    return bookmark.hotel
                })
            },

            views() {
                if (this.bookmarkedHotels.length > 0 || this.currentView === 'bookmarks') {
                    return ['search', 'bookmarks']
                }

                return ['search']
            },

            filtersCount() {
                let count = 0
                
                if (this.filters.is_refundable === false) {
                    count++
                }

                if (this.filters.is_package === true) {
                    count++
                }

                if (this.filters.is_member === true) {
                    count++
                }

                if (this.filters.property_types.length > 0) {
                    count++
                }

                if (this.filters.amenities.length > 0) {
                    count++
                }

                if (this.filters.distance_to_center.length > 0) {
                    count++;
                }

                if (this.filters.property_stars.length > 0) {
                    count++
                }

                if (this.filters.ratings !== null) {
                    count++
                }

                if (this.filters.price_range.length > 0) {
                    count++
                }

                if (this.filters.amenities.length > 0) {
                    count++;
                }

                return count
            },

            isMapVisible() {
                return this.screenWidth >= 992
            },
        },
        methods: {
            updateSize() {
                this.screenWidth = $(window).width()
            },

            toggleTemplate() {
                var self = this
                axios.post(route('api.frontend.search.toggleTemplate', {
                    search: this.currentSearch.token,
                }), {}).then(function (response) {
                    self.currentSearch = response.data.data
                })
            },

            paramsAvailable() {
                const params = this.$router.history.current.query

                return (
                    (typeof params.location !== 'undefined') && (
                        (typeof params.date_from !== 'undefined' && typeof params.date_to !== 'undefined') ||
                        typeof params.adults !== 'undefined' ||
                        typeof params.children !== 'undefined'
                        )
                    );
            },

            modificationParamsAvailable() {
                const params = this.$router.history.current.query

                return (
                    typeof params.page !== 'undefined' ||
                    typeof params.order !== 'undefined' ||
                    typeof params.property_stars !== 'undefined' ||
                    typeof params.property_types !== 'undefined' ||
                    typeof params.amenities !== 'undefined' ||
                    typeof params.distance_to_center !== 'undefined' ||
                    typeof params.ratings !== 'undefined' ||
                    typeof params.price_range !== 'undefined' ||
                    typeof params.filters !== 'undefined'
                )
            },

            loadSearch() {
                var self = this

                self.isLoading = true

                axios.get(route('api.frontend.search.show', {
                    search: this.token,
                }), {
                }).then(function (response) {
                    self.isLoading = false
                    
                    self.currentSearch = response.data.data
                    self.reflectCurrentSearch()

                    setTimeout(() => self.isMounted = true, 500)
                }).catch(function (error) {
                    // console.log(error);
                    self.isLoading = false
                });
            },

            resetSearch() {
                this.isSkipFirstParamsRefresh = true
                this.filters = {
                    is_refundable: true,
                    is_package: false,
                    is_member: false,
                    property_types: [],
                    amenities: [],
                    distance_to_center: [],
                    property_stars: [],
                    ratings: null,
                    price_range: [],
                }
                this.order = 'recommended'

                this.search()
            },

            mountParams() {
                const params = this.$router.history.current.query

                // Reset
                if (Object.keys(params).length === 0) {
                    this.resetSearch()
                    return;
                }

                // Init query params with default search token
                // if (typeof params.token !== 'undefined') {
                //     this.currentSearch = {
                //         token: params.token
                //     }
                // }

                // Location
                if (typeof params.location !== 'undefined') {
                    this.location = {
                        id: params.location
                    }
                }

                // Travel period
                if (typeof params.date_from !== 'undefined' && typeof params.date_to !== 'undefined') {
                    // Check if they are different
                    if (this.travelPeriod && this.travelPeriod.from && this.travelPeriod.to && this.travelPeriod.from.isSame(moment(params.date_from)) && this.travelPeriod.to.isSame(moment(params.date_to))) {
                        return;
                    }
                    
                    this.travelPeriod = {
                        from: moment(params.date_from),
                        to: moment(params.date_to),
                    }
                }

                // Current page
                if (typeof params.page !== 'undefined') {
                    this.currentPage = params.page
                }

                // Order
                if (typeof params.order !== 'undefined') {
                    this.order = params.order
                }

                // Persons
                let persons = []
                if (typeof params.adults !== 'undefined') {
                    persons.push({
                        type: 'adult',
                        amount: parseInt(params.adults, 10),
                        birthdate: null,
                    })
                }
                if (typeof params.children !== 'undefined') {
                    params.children.split(',').map((child) => ({
                        type: 'child',
                        amount: 1,
                        birthdate: child,
                    }))
                }
                if (persons.length > 0) {
                    this.persons = persons
                }

                // Ratings
                if (typeof params.ratings !== 'undefined') {
                    this.filters.ratings = params.ratings
                }

                // Property stars
                if (typeof params.property_stars !== 'undefined' && String(params.property_stars).length > 0) {
                    this.filters.property_stars = params.property_stars.split(',').map((value) => parseInt(value, 10))
                }

                // Property types
                if (typeof params.property_types !== 'undefined' && String(params.property_types).length > 0) {
                    this.filters.property_types = params.property_types.split(',').map((value) => parseInt(value, 10))
                }

                // Amenities
                if (typeof params.amenities !== 'undefined' && String(params.amenities).length > 0) {
                    this.filters.amenities = params.amenities.split(',')
                }

                // Distance to center
                if (typeof params.distance_to_center !== 'undefined') {
                    this.filters.distance_to_center = params.distance_to_center.split(',').map(value => parseInt(value, 10))
                }

                // Price range
                if (typeof params.price_range !== 'undefined') {
                    this.filters.price_range = params.price_range.split(',').map((range) => {
                        return range.split('-').map((value) => parseFloat(value))
                    })
                }

                // Filters
                if (typeof params.filters !== 'undefined') {
                    const filters = params.filters.split(',')

                    // None refundable
                    if (filters.indexOf('none_refundable') !== -1) {
                        this.filters.is_refundable = false
                    }

                    // Refundable
                    if (filters.indexOf('refundable') !== -1) {
                        this.filters.is_refundable = true
                    }

                    // Package
                    if (filters.indexOf('package') !== -1) {
                        this.filters.is_package = true
                    }

                    // Member
                    if (filters.indexOf('member') !== -1) {
                        this.filters.is_member = true
                    }
                }

                this.search()
            },
            hideDropdowns(exclude) {
                const targets = ['locationInput', 'travelPeriodPicker', 'personsPicker']

                targets.filter((target) => target !== exclude)
                .forEach((target) => {
                    if (this.$refs[target]) this.$refs[target].hideDropdown()
                })
            },
            handleChangeLocation(location) {
                this.location = location
                this.query = location.name

                this.resetPage()

                this.refreshParams()

                // Set order
                if (this.location.has_polygon) {
                    this.order = 'recommended';
                } else {
                    this.order = 'distance'
                }
            },
            handleChangeTravelPeriod(period) {
                this.travelPeriod = period

                if (this.travelPeriod && this.travelPeriod.from && ! this.travelPeriod.to) {
                    return;
                }
                
                this.resetPage()

                this.refreshParams()
            },
            handleChangePersons(persons) {
                this.persons = persons

                this.resetPage()

                this.refreshParams()
            },
            handleCenterProperty(property) {
                this.centerProperty = property
            },
            handleToggleBookmarkProperty(property) {
                var self = this;

                axios.post(route('api.frontend.search.bookmarks.toggle', {
                    search: this.currentSearch.token,
                }), {
                    type: 'hotel',
                    id: property.id,
                }).then(function (response) {
                    self.currentSearch.bookmarks = response.data.data

                    // Modify data set
                    const bookmarkIds = self.currentSearch.bookmarks.map((property) => property.hotel.id)
                    self.data = self.data.map((property) => {
                        property.is_bookmarked = bookmarkIds.indexOf(property.id) !== -1

                        return property;
                    })
                    self.highlightedProperties = self.highlightedProperties.map((property) => {
                        property.is_bookmarked = bookmarkIds.indexOf(property.id) !== -1

                        return property;
                    })
                }).catch(function (error) {
                    console.log(error);
                });
            },
            handleLoadMapProperties(bounds) {
                this.search(bounds)
            },
            handleCloseFilters() {
                this.isShowFilters = false
                this.scrollTop()
            },
            handleChangeFilters(updatedFilters) {
                this.filters = updatedFilters
            },

            changeOrder(order) {
                this.order = order

                this.resetPage()
            },

            search(bounds) {
                var self = this;

                if (! this.location && this.$refs.locationInput) {
                    this.$refs.locationInput.focus()

                    return;
                }

                // Check if only travel_period_from is set and set departure date one day later
                if (this.travelPeriod && this.travelPeriod.from && ! this.travelPeriod.to) {
                    this.travelPeriod = {
                        from: this.travelPeriod.from,
                        to: this.travelPeriod.from.clone().add(1, 'days'),
                    };
                }

                // Check for hotel redirect
                if (this.location && this.location.hotel) {
                    const hotelParams = {
                        date_from: this.travelPeriod ? this.travelPeriod.from.format('YYYY-MM-DD') : null,
                        date_to: this.travelPeriod ? this.travelPeriod.to.format('YYYY-MM-DD') : null,
                        adults: this.$lodash.sumBy(this.persons.filter((person) => person.type === 'adult'), 'amount'),
                        children: this.persons.filter((person) => person.type === 'child').map((child) => child.birthdate),
                    };
                    if (hotelParams.children === null || hotelParams.length === 0) {
                        delete hotelParams.children
                    }

                    this.$window.location.href = this.location.hotel.url + '?' + new URLSearchParams(hotelParams).toString();
                    
                    return;
                }

                // Load search if didn't happen yet
                if (! this.currentSearch) {
                    this.loadSearch()
                    return;
                }

                // Set current view
                this.currentView = 'search'

                // Check last search
                const params = {
                    token: this.currentSearch.token,
                    region_id: ! bounds ? (this.location ? parseInt(this.location.id, 10) : null) : null,
                    bounds_latitude: bounds ? bounds.map((position) => position.latitude) : null,
                    bounds_longitude: bounds ? bounds.map((position) => position.longitude) : null,
                    travel_period_from: this.travelPeriod ? this.travelPeriod.from.format('YYYY-MM-DD') : null,
                    travel_period_to: this.travelPeriod ? this.travelPeriod.to.format('YYYY-MM-DD') : null,
                    adults: this.$lodash.sumBy(this.persons.filter((person) => person.type === 'adult'), 'amount'),
                    children: this.persons.filter((person) => person.type === 'child').map((child) => child.birthdate),
                    order: this.order,
                    filters: [
                    this.filters.is_refundable ? 'refundable' : null,
                    this.filters.is_package ? 'package' : null,
                    this.filters.is_member ? 'member' : null,
                    ].filter(filter => filter), // Filter null values
                    property_types: this.filters.property_types,
                    amenities: this.filters.amenities,
                    property_scope: this.filters.property_scope,
                    property_stars: this.filters.property_stars,
                    ratings: this.filters.ratings,
                    price_range: this.filters.price_range.map((range) => range.join(',')),
                    distance_to_center: this.filters.distance_to_center,
                    page: parseInt(this.currentPage, 10) || 1,
                };

                // Prevent request if it's identical with the last one
                if (JSON.stringify(params) === this.lastSearch) {
                    return;
                }

                // Remember last params
                this.lastSearch = JSON.stringify(params)

                // Cancel previous search
                if (this.cancelSource !== null) {
                    this.cancelSource.cancel()
                }

                // Create cancel token
                const CancelToken = axios.CancelToken
                this.cancelSource = CancelToken.source()

                this.isLoading = true

                axios.get(route('api.frontend.search'), {
                    params: params,
                    cancelToken: this.cancelSource.token,
                }).then(function (response) {
                    self.data = response.data.data
                    self.meta = response.data.meta
                    self.highlightedProperties = response.data.highlighted_properties || [];

                    if (! self.currentPage) {
                        self.currentPage = response.data.meta.current_page
                    }

                    // Property types
                    self.propertyTypes = response.data.property_types || []

                    // Amenities
                    self.amenities = response.data.amenities || []

                    // Additional filters
                    self.additionalFilters = response.data.additional_filters || []

                    // Scroll top
                    // self.scrollTop()

                    // Set currentSearch
                    if (response.data.search) {
                        self.currentSearch = response.data.search
                    }

                    // Update location
                    if (! self.location || (self.location && self.location.id !== response.data.region.id)) {
                        self.location = response.data.region
                    }

                    // Skip first url refresh to prevent browser back issue
                    if (self.isSkipFirstParamsRefresh === false) {
                        self.refreshParams()
                    }
                    self.isSkipFirstParamsRefresh = false

                    self.isLoading = false

                    // Data layer: Enhanced ecommerce
                    self.trackProductImpressions(response.data.data)
                }).catch(function (error) {
                    // console.log(error);
                    self.isLoading = false
                });
            },

            scrollTop() {
                $('html,body').stop().animate({
                    scrollTop: $('div.nav-container').offset().top - 30
                }, 1000, 'swing');
            },
            
            trackProductImpressions(items) {
                // Clear previous ecommerce object
                this.$window.dataLayer.push({
                    ecommerce: null,
                })

                // Push updated data
                this.$window.dataLayer.push({
                    'event': 'eeProductImpression',
                    'ecommerce': {
                        'currencyCode': 'EUR',
                        'impressions': items.map((item, itemIndex) => ({
                            'id': 'watado-trips_' + item.id,
                            'name': item.name,
                            'price': item.from_price ? item.from_price.total : null,
                            'revenue': item.from_price ? item.from_price.total : null,
                            'tax': 0,
                            'shipping': 0,
                            'brand': 'watado-trips',
                            'category': (item.type ? item.type.name : 'Hotel') + ' - ' + item.city + ', ' + item.country,
                            'quantity': 1,
                            'list': 'Search Results',
                            'position': itemIndex + 1,
                            'affiliation': 'watado-trips',
                        }))
                    }
                })
            },

            // searchBounds(bound) {
            //     axios.get(route('api.frontend.search'), {
            //         params: {
            //             region_id: this.location.id,
            //         },
            //     }).then(function (response) {
            //         self.data = response.data.data
            //     }).catch(function (error) {
            //         console.log(error);
            //     });
            // },

            // Pagination
            prevPage() {
                if (this.currentPage > 1) {
                    this.currentPage = this.currentPage - 1
                    this.search()

                    var self = this
                    setTimeout(() => {
                        self.scrollTop()
                    }, 300)
                }
            },
            nextPage() {
                if (this.meta && this.meta.last_page && this.currentPage < this.meta.last_page) {
                    this.currentPage = parseInt(this.currentPage, 10) + 1
                    this.search()

                    var self = this
                    setTimeout(() => {
                        self.scrollTop()
                    }, 300)
                }
            },
            setPage(page) {
                this.currentPage = page
                this.search()

                var self = this
                setTimeout(() => {
                    self.scrollTop()
                }, 300)
            },
            resetPage() {
                if (this.currentPage) {
                    this.currentPage = 1
                }
            },

            selectRegion(region) {
                this.handleChangeLocation(region)

                this.search()

                var self = this
                setTimeout(() => {
                    self.scrollTop()
                }, 300)
            },
            selectPropertyType(propertyType) {
                // this.togglePropertyType(propertyType)

                this.search()

                var self = this
                setTimeout(() => {
                    self.scrollTop()
                }, 300)
            },

            // Copy link
            copyLink() {
                this.$window.copy(route('frontend.search.show', {
                    search: this.currentSearch.token,
                }))
                this.isCopied = true

                var self = this
                setTimeout(() => {
                    self.isCopied = false
                }, 2000)
            },

            copy(text) {
                if (! this.$window.navigator.clipboard) {
                    fallbackCopyTextToClipboard(text);
                    return;
                }
                
                this.$window.navigator.clipboard.writeText(text).then(function() {
                    console.log('Async: Copying to clipboard was successful!');
                }, function(err) {
                    console.error('Async: Could not copy text: ', err);
                });
            },
            copyFallback(text) {
                var textArea = this.$window.document.createElement("textarea");
                textArea.value = text;
                
                // Avoid scrolling to bottom
                textArea.style.top = "0";
                textArea.style.left = "0";
                textArea.style.position = "fixed";
                
                this.$window.document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                
                try {
                    var successful = this.$window.document.execCommand('copy');
                    var msg = successful ? 'successful' : 'unsuccessful';
                    console.log('Fallback: Copying text command was ' + msg);
                } catch (err) {
                    console.error('Fallback: Oops, unable to copy', err);
                }
                    
                this.$window.document.body.removeChild(textArea);
            },

            reflectCurrentSearch() {
                this.location = this.$lodash.find(this.currentSearch.regions, {
                    id: this.currentSearch.latest.region_id
                })
                if (this.currentSearch.latest.travel_period_from && this.currentSearch.latest.travel_period_to) {
                    this.travelPeriod = {
                        from: moment(this.currentSearch.latest.travel_period_from),
                        to: moment(this.currentSearch.latest.travel_period_to),
                    }
                } else {
                    // Open travel period picker of no date was provided
                    this.$refs.travelPeriodPicker.toggleCheckIn()
                }
                this.query = this.location.name
                this.persons = this.currentSearch.persons.map((person) => {
                    return {
                        type: person.type,
                        birthdate: person.birthdate,
                        amount: person.quantity,
                    }
                })
                
                // Filters
                if (this.currentSearch.latest.filters) {
                    const guardedFilters = this.currentSearch.latest.filters
                    delete guardedFilters.order

                    this.filters = {...this.filters, ...this.currentSearch.latest.filters}
                }

                this.search()
            },

            refreshParams() {
                if (! this.isMounted) {
                    return;
                }

                let queryParams = {}

                // Location
                if (this.location) {
                    queryParams['location'] = String(this.location.id)
                }

                // Travel period
                if (this.travelPeriod) {
                    queryParams['date_from'] = this.travelPeriod.from.format('YYYY-MM-DD')
                    queryParams['date_to'] = this.travelPeriod.to.format('YYYY-MM-DD')
                }

                // Current page
                if (this.currentPage) {
                    queryParams['page'] = String(this.currentPage)
                }

                // Order
                if (this.order) {
                    queryParams['order'] = this.order
                }

                // Filters
                const filters = []

                // Refundable
                if (this.filters.is_refundable) {
                    filters.push('refundable')
                } else {
                    filters.push('none_refundable')
                }

                // Package
                if (this.filters.is_package) {
                    filters.push('package')
                }

                // Member
                if (this.filters.is_member) {
                    filters.push('member')
                }

                // Apply filters to url query
                if (filters.length > 0) {
                    queryParams['filters'] = filters.join(',')
                }

                // Persons
                if (this.persons && this.persons.length > 0) {
                    queryParams['adults'] = String(this.$lodash.sumBy(this.persons.filter((person) => person.type === 'adult'), 'amount'))

                    // Children (only if available)
                    if (this.persons.filter((person) => person.type === 'child').length > 0) {
                        queryParams['children'] = this.persons.filter((person) => person.type === 'child').map((child) => child.birthdate).join(',')
                    }
                }

                // Ratings
                if (this.filters.ratings) {
                    queryParams['ratings'] = String(this.filters.ratings)
                }

                // Property stars
                if (this.filters.property_stars && this.filters.property_stars.length > 0) {
                    queryParams['property_stars'] = String(this.filters.property_stars.join(','))
                }

                // Property types
                if (this.filters.property_types && this.filters.property_types.length > 0) {
                    queryParams['property_types'] = String(this.filters.property_types.join(','))
                }

                // Amenities
                if (this.filters.amenities && this.filters.amenities.length > 0) {
                    queryParams['amenities'] = String(this.filters.amenities.join(','))
                }

                // Distance to center
                if (this.filters.distance_to_center && this.filters.distance_to_center.length > 0) {
                    queryParams['distance_to_center'] = String(this.filters.distance_to_center.join(','))
                }

                // Price range
                if (this.filters.price_range && this.filters.price_range.length > 0) {
                    queryParams['price_range'] = this.filters.price_range
                    .map((range) => range.join('-'))
                    .join(',')
                }

                // Prevent equal params
                if (this.$lodash.isEqual(this.$router.history.current.query, queryParams)) {
                    return;
                }

                // Push updated route details to browser history
                this.$router.push({
                    path: this.$route.path,
                    // name: this.$route.name,
                    mode: 'history',
                    query: queryParams,
                }).catch(err => {
                    console.log(err)
                    if (err.name !== 'NavigationDuplicated' && ! err.message.includes('Avoided redundant navigation to current location')) {
                        console.log(err);
                    }
                });
            },

            removeSearchRegion(searchRegion, searchRegionIndex) {
                var self = this;

                axios.delete(route('api.frontend.search.regions.travelPeriods.delete', {
                    search: this.currentSearch.token,
                    region: searchRegion.id,
                    searchTravelPeriod: searchRegion.travel_period.id,
                }), {}).then(function (response) {
                    self.currentSearch = response.data.data

                    // Check if active location was deleted
                    if (self.location && self.currentSearch && self.currentSearch.regions.length > 0) {
                        const exists = self.$lodash.findIndex(self.currentSearch.regions, {
                            id: self.location.id
                        }) !== -1

                        if (! exists) {
                            self.selectSearchRegion(self.currentSearch.regions[0])
                        }
                    }
                }).catch(function (error) {
                    console.log(error);
                });
            },
            selectSearchRegion(searchRegion) {
                this.selectRegion(searchRegion)

                if (searchRegion.travel_period) {
                    this.handleChangeTravelPeriod({
                        from: this.$moment(searchRegion.travel_period.date_from),
                        to: this.$moment(searchRegion.travel_period.date_to)
                    })
                    this.$refs.travelPeriodPicker.setTravelPeriod({
                        from: this.$moment(searchRegion.travel_period.date_from),
                        to: this.$moment(searchRegion.travel_period.date_to)
                    })
                } else {
                    this.handleChangeTravelPeriod(null)
                    this.$refs.travelPeriodPicker.setTravelPeriod(null)
                }
            },
            activeSearchRegion(searchRegion) {
                return (this.location && this.location.id == searchRegion.id) && (
                    (! searchRegion.travel_period && ! this.travelPeriod) || (
                        searchRegion.travel_period && this.travelPeriod && this.$moment(searchRegion.travel_period.date_from).isSame(this.travelPeriod.from) && this.$moment(searchRegion.travel_period.date_to).isSame(this.travelPeriod.to)
                        )
                    )
            },
        },
        watch: {
            $route (to, from) {
                if (! this.isMounted) {
                    return;
                }

                this.mountParams()
            },

            location(newValue, oldValue) {
                // Prevent type issue
                if (oldValue === null || (newValue && oldValue && String(newValue.id) === String(oldValue.id))) {
                    return;
                }

                // Reset page
                // this.resetPage()

                if (this.currentSearch) {
                    this.search()
                }
            },
            travelPeriod: {
                handler(val, oldValue) {
                    // Prevent initial change
                    if (oldValue === null) {
                        return;
                    }

                    // Prevent auto loading if only from date is set
                    if (val && val.from && ! val.to) {
                        return;
                    }
                    
                    // Reset page
                    // this.resetPage()

                    if (this.currentSearch) {
                        this.search()
                    }
                },
                deep: true
            },
            filters: {
                handler(val, oldValue) {
                    if (! this.isMounted) {
                        return;
                    }

                    // Reset page
                    // this.resetPage()

                    if (this.currentSearch) {
                        this.search()
                    }
                },
                deep: true
            },
            persons: {
                handler(val, oldValue) {
                    if (oldValue.length === 0) {
                        return;
                    }

                    // Reset page
                    // this.resetPage()

                    if (this.currentSearch) {
                        this.search()
                    }
                },
                deep: true
            },
            order(newValue, oldValue) {
                // Reset page
                // this.resetPage()

                if (this.currentSearch) {
                    this.search()
                }
            },
            currentSearch: {
                handler(val) {
                    if (! this.currentSearch) {
                        return;
                    }

                    // Check for token change and replace history
                    if (this.currentSearch.token !== this.token) {
                        this.$router.push({
                            path: this.currentSearch.token,
                            // name: this.$route.name,
                            // mode: 'history',
                            query: {},
                        }).catch(err => {
                            console.log(err)
                            if (err.name !== 'NavigationDuplicated' && ! err.message.includes('Avoided redundant navigation to current location')) {
                                console.log(err);
                            }
                        });

                        // this.$window.location.href = this.$window.route('frontend.search.show', {
                        //     search: this.currentSearch.token,
                        // })

                        return;
                    }

                    if (this.currentSearch) {
                        const el = document.querySelector(".filters-container")
                        if (el) {
                            const observer = new IntersectionObserver(
                                ([e]) => e.target.classList.toggle("is-sticky", e.intersectionRatio < 1),
                                { threshold: [1] }
                                );
                            observer.observe(el);
                        }
                    }
                },
                deep: true
            },
            currentView() {
                this.scrollTop()
            },
        }
    }
</script>
